import React from 'react';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { CheckBox } from '../../tools/controls';
import SearchComputeTable from './search-compute-table';
import Scrollbox from '../../tools/scrollbox';
import CollapsingPanel, {
  PanelPosition,
} from '../../tools/collapsing-panel/collapsing-panel';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
} from '../../utils/events';
import { availabilityOptions } from '../../utils/common/constants';

// enum.

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  setAvailabilityType,
  setDescription,
  setFastScratch,
  setGPU,
  setHardwareType,
  setHighMemory,
  setLargeScratch,
  setNodeValue,
  setServiceType,
  setSiteValue,
} from './search-compute-slice';
import { Site } from '../../utils/types';
import { handleEvent } from '../../app-actions';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function SearchCompute() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_LoggedInRefreshTrigger: number | undefined = useAppSelector(
    (state) => state.global.loggedInRefreshTrigger,
  );

  // data state variables.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_serviceTypesList: string[] = useAppSelector(
    (state) => state.data.serviceTypes,
  );

  const sDescription: string = useAppSelector(
    (state) => state.searchCompute.sDescription,
  );
  const sGPU: boolean = useAppSelector((state) => state.searchCompute.sGPU);
  const sLargeScratch: boolean = useAppSelector(
    (state) => state.searchCompute.sLargeScratch,
  );
  const sHighMemory: boolean = useAppSelector((state) => state.searchCompute.sHighMemory);
  const sFastScratch: boolean = useAppSelector(
    (state) => state.searchCompute.sFastScratch,
  );
  const sNodeValue: string = useAppSelector((state) => state.searchCompute.sNodeValue);
  const sSiteValue: string = useAppSelector((state) => state.searchCompute.sSiteValue);
  const sHardwareType: string = useAppSelector(
    (state) => state.searchCompute.sHardwareType,
  );
  const sServiceType: string = useAppSelector(
    (state) => state.searchCompute.sServiceType,
  );
  const sAvailabilityType: string = useAppSelector(
    (state) => state.searchCompute.sAvailabilityType,
  );
  const sMiddlewareVersion: string = useAppSelector(
    (state) => state.searchCompute.sMiddlewareVersion,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  const availability = availabilityOptions(t);

  //  ------------------------------------------------------------
  //
  //  Handler for changes to the radio buttons.
  //
  //  ------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.currentTarget;

    // if we click a search button on the search form then open a new results tab.
    if (inputBox.name === 'searchData') {
      const params: InitiateComputeSearchParams = {
        node: sNodeValue,
        site: sSiteValue,
        gpu: sGPU,
        largeScratch: sLargeScratch,
        highMemory: sHighMemory,
        fastScratch: sFastScratch,
        description: sDescription,
        hardwareType: sHardwareType,
        middlewareVersion: sMiddlewareVersion,
        serviceType: sServiceType,
        availabilityType: sAvailabilityType,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_COMPUTE_TABLE,
        eventType: EventType.INITIATE_COMPUTE_SEARCH,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  }; // onClickHandler

  //  -------------------------------------------------
  //
  //  function that handles a change in the selected
  //  service type on the search form
  //
  //  -------------------------------------------------

  const serviceTypeOnChangeHandler = function (args: { serviceType: string }) {
    dispatch(setServiceType(args.serviceType));
  }; // serviceTypeOnChangeHandler

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div
      style={{
        flex: '1 1',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* the column containing filter boxes, etc. */}
      <CollapsingPanel
        className="br-d-r"
        position={PanelPosition.LEFT}
        title={t('Filter')}
        height="100%"
        width="310px"
      >
        <Scrollbox
          direction="vertical"
          height="100%"
          flex="0 0 auto"
          flexDirection="column"
          padding="10px 8px 10px 8px"
        >
          <div
            style={{
              padding: '8pt',
              width: '100%',
              gap: '5px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/*
            /// node
            */}
            <label id="search-compute-node-label" className="text-colour">
              Node:
            </label>
            <select
              aria-labelledby="search-compute-node-label"
              className="listbox"
              multiple={false}
              value={sNodeValue}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(setNodeValue(e.target.value))
              }
              data-placeholder-shown={sNodeValue === '' ? 'T' : 'F'}
            >
              <option hidden value="">
                {t('Select node')}
              </option>
              <option label="All" value="all">
                {' '}
                All{' '}
              </option>
              {sD_sites
                .filter(
                  (itm, idx) =>
                    sD_sites.findIndex((fnd) => fnd.node === itm.node) === idx,
                )
                .map((item) => (
                  <option key={item.node} label={item.node} value={item.node}>
                    {' '}
                    {item.node}{' '}
                  </option>
                ))}
            </select>

            {/*
                /// site
                */}
            <label
              id="search-compute-site-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              Site:
            </label>
            <select
              className="listbox"
              multiple={false}
              value={sSiteValue}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(setSiteValue(e.target.value))
              }
              data-placeholder-shown={sSiteValue === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%' }}
            >
              <option hidden value="">
                {t('Select site')}
              </option>
              <option label="All" value="all">
                {' '}
                All{' '}
              </option>
              {sD_sites
                .filter(
                  (itm, idx) =>
                    itm.node === sNodeValue &&
                    sD_sites.findIndex((fnd) => fnd.site_uuid === itm.site_uuid) === idx,
                )
                .map((item) => (
                  <option key={item.site} label={item.site} value={item.site}>
                    {' '}
                    {item.site}{' '}
                  </option>
                ))}
            </select>

            {/*
                /// description
                */}
            <label
              id="search-compute-description-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              Description:
            </label>
            <input
              aria-labelledby="search-compute-description-label"
              className="input-field"
              type="text"
              placeholder={t('Description')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(setDescription(e.target.value))
              }
              maxLength={50}
              value={sDescription}
            ></input>

            {/*
            /// hardware capabilities
            */}
            <label
              id="search-compute-hardware-capabilities-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Hardware capabilities')}:
            </label>
            <div
              role="group"
              aria-labelledby="search-compute-hardware-capabilities-label"
              className="container"
              style={{
                display: 'flex',
                padding: '0px',
              }}
            >
              <CheckBox
                key={t('GPU')}
                value="GPU"
                text={t('GPU')}
                checked={sGPU === true}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(setGPU(e.target.checked))
                }
              />
              <CheckBox
                key={t('High memory')}
                value="highMemory"
                text={t('High memory')}
                checked={sHighMemory === true}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(setHighMemory(e.target.checked))
                }
              />
              <CheckBox
                key={t('Large scratch')}
                value="largeScratch"
                text={t('Large scratch')}
                checked={sLargeScratch === true}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(setLargeScratch(e.target.checked))
                }
              />
              <CheckBox
                key={t('Fast scratch')}
                value="fastScratch"
                text={t('Fast scratch')}
                checked={sFastScratch === true}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(setFastScratch(e.target.checked))
                }
              />
            </div>

            {/*
            /// hardware type
            */}
            <label
              id="search-compute-hardware-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Hardware type')}:
            </label>
            <select
              aria-labelledby="search-compute-hardware-type-label"
              className="listbox"
              multiple={false}
              value={sHardwareType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(setHardwareType(e.target.value))
              }
              data-placeholder-shown={sHardwareType === '' ? 'T' : 'F'}
              style={{
                lineHeight: '100%',
              }}
            >
              <option hidden value="">
                {t('Select hardware type')}
              </option>
              <option label={t('Any')} value="any">
                {' '}
                Any{' '}
              </option>
              <option label={t('Container')} value="container">
                {' '}
                Container{' '}
              </option>
              <option label={t('Bare metal')} value="bare_metal">
                {' '}
                Bare Metal{' '}
              </option>
              <option label={t('Virtual machine')} value="vm">
                {' '}
                Virtual Machine{' '}
              </option>
            </select>

            {/*
            /// availability type
            */}
            <label
              id="search-compute-availability-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Availability type')}:
            </label>
            <select
              aria-labelledby="search-compute-availability-type-label"
              name="lst-availability-type"
              className="listbox"
              multiple={false}
              value={sAvailabilityType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(setAvailabilityType(e.target.value))
              }
              data-placeholder-shown={sAvailabilityType === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%' }}
            >
              <option hidden value="">
                {t('Select availability type')}
              </option>
              {availability.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            {/*
            /// service type
            */}
            <label
              id="search-compute-service-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Service type')}:
            </label>
            <select
              aria-labelledby="search-compute-service-type-label"
              className="listbox"
              multiple={false}
              value={sServiceType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(setServiceType(e.target.value))
              }
              data-placeholder-shown={sServiceType === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%', borderRadius: '0px' }}
            >
              <option hidden value="">
                {t('Select service type')}
              </option>
              <option label="All" value="all">
                {' '}
                All{' '}
              </option>
              {sD_serviceTypesList.map((item, index) => (
                <option key={index.toString()} label={item} value={item}>
                  {' '}
                  {item}{' '}
                </option>
              ))}
            </select>
          </div>

          <div style={{ margin: '15px 10px 10px 10px' }}>
            <ToolButton
              key={t('Search')}
              name="searchData"
              onClick={onClickHandler}
              text={t('Search')}
              type={ToolButtonType.SECONDARY}
            />
          </div>
        </Scrollbox>
      </CollapsingPanel>

      {/*
       //
       //	the SearchComputeTable component runs a search query with the given parameters, and displays the results in a table.
       //
       */}
      <div className="search-compute-table">
        <div style={{ flex: '0 0 10px' }} />
        <SearchComputeTable />
        <div style={{ flex: '0 0 10px' }} />
      </div>
      <div style={{ flex: '0 0 10px' }} />
    </div>
  );
  //
} // SearchCompute
